import React, {
  ReactElement,
  useState,
  useRef,
  useContext,
  createContext,
  useEffect,
  PropsWithChildren,
  useCallback,
  useMemo,
} from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { matchPath } from 'react-router';
import { useHistory, useLocation } from 'src/router';
import qs from 'qs';
import { DNAIcon, luxColors, DNABox, util } from '@alucio/lux-ui';
import { OverflowMenu, Input } from '@ui-kitten/components';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import InputComponent from '../../Publishers/InputComponent';
import { useCanPerformSearch } from 'src/state/redux/selector/documentSearch/documentSearch';
import { useAppSettings } from 'src/state/context/AppSettings';
import useIsPublisherView from 'src/hooks/useIsPublisherView/useIsPublisherView';
import useIsDebugView from 'src/hooks/useIsDebugView/useIsDebugView';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import ROUTES from 'src/router/routeDef';
import { buildSearchRoute, getSearchRouteText } from 'src/utils/searchRouteText';
import SearchInputResultsDesktop from './SearchInputResultsDesktop';
import SearchTypeToggle from './SearchTypeToggle';
import AISearchFlowSelector from './aiSearch/AISearchFlowSelector';
import AISearchHistoryControls from './aiSearch/AISearchHistoryControls';

import {
  useDocumentSearchV2Context,
} from 'src/hooks/useDocumentSearchV2';
import { DocumentSearchProxyProvider } from 'src/hooks/useDocumentSearchV2.proxy';

export interface SearchInputProps {
  searchText: string,
}

const SEARCH_SUBMIT_MODE = {
  current: 'current',
  regular: 'regular',
  ai: 'ai',
} as const;

type SearchSubmitMode = typeof SEARCH_SUBMIT_MODE[keyof typeof SEARCH_SUBMIT_MODE]

export const SearchInputContext = createContext<SearchInputProps>({ searchText: '' })

const styles = StyleSheet.create({
  overflowMenu: {
    maxHeight: '85vh',
  },
  searchBarStyle: {
    backgroundColor: luxColors.info.primary,
    borderColor: luxColors.disabled.secondary,
    borderRadius: 4,
    maxWidth: '100%',
    overflow: 'hidden',
    paddingHorizontal: 2,
  },
  searchBarStyleUI2: {
    borderRadius: 100,
    borderColor: colors['color-ui2-neutral-300'],
    overflow: 'hidden',
    backgroundColor: colors['color-white'],
    minHeight: 40,
    height: 48,
    paddingHorizontal: 8,
  },
  searchBarText: {
    borderRadius: 0,
    width: '100%',
    maxHeight: 40,
    paddingHorizontal: 0,
  },
  searchBarWrapper: {
    marginTop: 3,
    flex: 1,
  },
  popover: {
    backgroundColor: colors['color-black'],
  },
  popoverContent: {
    padding: 8,
  },
  popoverText: {
    color: colors['color-text-white'],
  },
  popoverBodyHeader: {
    color: colors['color-text-white'],
    textDecorationLine: 'underline',
  },
  popoverCloseBtn: {
    backgroundColor: colors['color-gray-600'],
    borderRadius: 4,
  },
  popoverCloseIcon: {
    color: colors['color-text-white'],
    width: 35,
    height: 35,
  },
});

const ICONS = {
  CLOSE: 'x',
  MARGNIFY: 'magnifying-glass',
  LIGHT: 'question',
};

const INITIAL_MAX_WIDTH = 600;

const getRouteScopedSearchText = ({
  currentSearchString,
  isPublisherView,
  queryParamSearchString,
}: {
  currentSearchString: string;
  isPublisherView: boolean;
  queryParamSearchString: string;
}) => {
  return isPublisherView ? queryParamSearchString : currentSearchString;
};

const useSearchInput = () => {
  const isPublisherView = useIsPublisherView();
  const isDebugView = useIsDebugView();
  const { currentSearchString, setCurrentSearchString } = useSearchTextContextProvider();
  const { isAiSearch: isAiSearchActive } = useDocumentSearchV2Context();
  const { pathname, search } = useLocation();
  const qsParam = qs.parse(search, { ignoreQueryPrefix: true })
  const isPublisherSearchView = pathname.toLowerCase() === ROUTES.SEARCH_RESULTS_PUBLISHER.PATH
  const queryParamSearchString = typeof qsParam.searchText === 'string' ? qsParam.searchText : ''
  const [searchText, setSearchText] = useState<string>(
    isPublisherView
      ? queryParamSearchString
      : currentSearchString,
  )
  const canPerformSearch = useCanPerformSearch();
  const [displaySearchResults, setDisplaySearchResults] = useState<boolean>(false);
  const inputRef = useRef<Input>(null)
  const history = useHistory();
  const draftUrlTimer = useRef<ReturnType<typeof setTimeout>>();

  const cancelDraftUrlUpdate = useCallback(() => {
    clearTimeout(draftUrlTimer.current);
    draftUrlTimer.current = undefined;
  }, []);

  useEffect(() => {
    const unlisten = history.listen((location, action) => {
      if (action !== 'REPLACE') cancelDraftUrlUpdate();
      if (action === 'POP' && !isPublisherView && !isDebugView) {
        setSearchText(getInitialSearchFromUrl(location.pathname, location.search));
        setDisplaySearchResults(false);
      }
    });
    return () => {
      unlisten();
      cancelDraftUrlUpdate();
    };
  }, [history, cancelDraftUrlUpdate, isPublisherView, isDebugView]);

  function updateDraftUrl(text: string): void {
    const location = history.location;
    const params = qs.parse(location.search, { ignoreQueryPrefix: true });
    if (text ? params.draftSearchText === text : params.draftSearchText === undefined) return;
    if (text) {
      params.draftSearchText = text;
    } else {
      delete params.draftSearchText;
    }
    // Keep the submitted query unchanged; history v4 must not decode the pathname again.
    history.replace({ search: qs.stringify(params), hash: location.hash, state: location.state });
  }

  useEffect(() => {
    setSearchText(getRouteScopedSearchText({
      currentSearchString,
      isPublisherView,
      queryParamSearchString,
    }));
    setDisplaySearchResults(false);
  }, [currentSearchString, isPublisherView, queryParamSearchString]);

  function onChangeText(text: string): void {
    const canSearch = canPerformSearch(text.trim());
    setSearchText(text);
    cancelDraftUrlUpdate();
    if (!isPublisherView && !isDebugView) {
      draftUrlTimer.current = setTimeout(() => updateDraftUrl(text), 500);
    }

    if (isAiSearchActive) {
      return;
    }

    if (!isPublisherView) {
      if (!displaySearchResults && canSearch) {
        setDisplaySearchResults(true);
      } else if (displaySearchResults && !canSearch) {
        setDisplaySearchResults(false);
      }
    }
    else if (!isPublisherSearchView && canSearch) {
      history.push(`${ROUTES.SEARCH_RESULTS_PUBLISHER.PATH}?searchText=${text}`);
    }
  }

  function clearText(): void {
    cancelDraftUrlUpdate();
    if (!isPublisherView && !isDebugView) updateDraftUrl('');
    setSearchText('');
    setCurrentSearchString('');
    inputRef.current?.focus()
  }

  function toggleSearchResultComponent(open: boolean): void {
    setDisplaySearchResults(open);
  }

  function resetSearch(): void {
    setSearchText('')
    setDisplaySearchResults(false)
  }

  return {
    displaySearchResults,
    onChangeText,
    clearText,
    toggleSearchResultComponent,
    searchText,
    setSearchText,
    setCurrentSearchString,
    cancelDraftUrlUpdate,
    resetSearch,
    setDisplaySearchResults,
    canPerformSearch,
    inputRef,
    isPublisherView,
    isDebugView,
  }
}

interface UseSearchInputIconsParams {
  isPublisherView: boolean;
  isV2SearchEnabled: boolean;
  isAiSearchActive: boolean;
  setIsAiSearchContext: (value: boolean) => void;
  searchText: string;
  moveToResultsPage: (mode?: SearchSubmitMode) => void;
  onSelectItemHandler: () => void;
  dispatchAiSearch: any;
  setDisplaySearchResults: (value: boolean) => void;
  isOnline: boolean;
}

const useSearchInputIcons = ({
  isPublisherView,
  isV2SearchEnabled,
  isAiSearchActive,
  setIsAiSearchContext,
  searchText,
  moveToResultsPage,
  onSelectItemHandler,
  dispatchAiSearch,
  setDisplaySearchResults,
  isOnline,
}: UseSearchInputIconsParams) => {
  const renderLeftIcon = useCallback(
    () => {
      if (isPublisherView || !isV2SearchEnabled) {
        return <DNAIcon.Styled name={ICONS.MARGNIFY} status="subtle" size="lg" />;
      }
      return (
        <SearchTypeToggle
          isAiSearchActive={isAiSearchActive}
          isAiSearchDisabled={!isOnline}
          onPressSearch={() => {
            setIsAiSearchContext(false);
            if (searchText.trim()) {
              moveToResultsPage(SEARCH_SUBMIT_MODE.regular);
            }
          }}
          onPressAiSearch={() => {
            setIsAiSearchContext(true);
            dispatchAiSearch({ type: 'RESET' });
            setDisplaySearchResults(false);
          }}
        />
      );
    },
    [
      dispatchAiSearch,
      isAiSearchActive,
      isPublisherView,
      isV2SearchEnabled,
      moveToResultsPage,
      searchText,
      setDisplaySearchResults,
      setIsAiSearchContext,
      isOnline,
    ],
  );

  const renderCloseSearchIcon = useCallback(
    () => {
      return (
        <TouchableOpacity
          onPress={onSelectItemHandler}
          // @ts-ignore - onMouseDown is used for web to prevent input blur which would trigger the collapse animation
          onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
        >
          <DNAIcon.Styled name={ICONS.CLOSE} status="subtle" size="lg" />
        </TouchableOpacity>
      );
    },
    [onSelectItemHandler],
  );

  return {
    renderLeftIcon,
    renderCloseSearchIcon,
  };
};

const SearchInput = () => {
  const { deviceMode, isOnline } = useAppSettings();
  const history = useHistory();
  const isDesktop = deviceMode === 'desktop';

  const {
    onChangeText,
    displaySearchResults,
    clearText,
    toggleSearchResultComponent,
    searchText,
    setCurrentSearchString,
    cancelDraftUrlUpdate,
    setDisplaySearchResults,
    canPerformSearch,
    inputRef,
    isPublisherView,
    isDebugView,
  } = useSearchInput();

  const [isFocused, setIsFocused] = useState(false);

  const contextValues = useDocumentSearchV2Context();
  const {
    isAiSearch: isAiSearchActive,
    setIsAiSearch: setIsAiSearchContext,
    dispatchAiSearch,
  } = contextValues;

  const isDebugPage = useIsDebugView();

  const moveToResultsPage = useCallback(
    (mode: SearchSubmitMode = SEARCH_SUBMIT_MODE.current) => {
      if (canPerformSearch(searchText.trim()) && !isPublisherView && !isDebugPage) {
        cancelDraftUrlUpdate();
        setCurrentSearchString(searchText);
        setDisplaySearchResults(false);

        const shouldQueueAiSearch = mode === SEARCH_SUBMIT_MODE.ai ||
          (mode === SEARCH_SUBMIT_MODE.current && isAiSearchActive);

        if (shouldQueueAiSearch) {
          dispatchAiSearch({ type: 'QUEUE_SEARCH', payload: searchText });
        }

        history.push(buildSearchRoute(searchText));

        analytics?.track('SEARCH_INPUT', {
          category: 'SEARCH',
          action: 'INPUT',
          searchType: isAiSearchActive ? 'searchQA' : 'hybrid',
          searchText,
        });
      }
    },
    // [NOTE] - This isn't really optimized, due to input updates on every keystroke
    [
      canPerformSearch,
      isPublisherView,
      isDebugPage,
      history,
      searchText,
      setCurrentSearchString,
      cancelDraftUrlUpdate,
      isAiSearchActive,
      dispatchAiSearch,
      setDisplaySearchResults,
      isOnline,
    ],
  );

  const { BEAC_7644_AI_search, BEAC_8091_AI_search_view_only } =
    useFeatureFlags('BEAC_7644_AI_search', 'BEAC_8091_AI_search_view_only');
  const isV2SearchEnabled = BEAC_7644_AI_search || BEAC_8091_AI_search_view_only;

  const onSelectItemHandler = useCallback(() => {
    setDisplaySearchResults(false);
    clearText();
  }, [clearText, setDisplaySearchResults]);

  const { renderLeftIcon, renderCloseSearchIcon } = useSearchInputIcons({
    isPublisherView,
    isV2SearchEnabled,
    isAiSearchActive,
    setIsAiSearchContext,
    searchText,
    moveToResultsPage,
    onSelectItemHandler,
    dispatchAiSearch,
    setDisplaySearchResults,
    isOnline,
  });

  function renderSearchInput(): ReactElement {
    return (
      <DNABox fill>
        <View style={{ maxWidth: isDesktop ? '100%' : INITIAL_MAX_WIDTH, width: '100%' }}>
          <InputComponent
            ref={inputRef}
            testID="header-search-input"
            value={searchText}
            hideLabel={true}
            onChangeText={onChangeText}
            placeHolder={isAiSearchActive ? 'Ask about your documents' : 'Search...'}
            removeMarginPadding={true}
            inputStyle={[
              styles.searchBarStyleUI2,
              isFocused && { borderColor: colors['color-ui2-brand-600'] },
            ]}
            textStyle={styles.searchBarText}
            getLeftIconFunction={renderLeftIcon}
            getRightIconFunction={searchText ? renderCloseSearchIcon : undefined}
            onSubmitEditing={() => moveToResultsPage()}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            autoCompleteType="off"
            size={deviceMode === 'desktop' ? 'md' : 'lg'}
          />
        </View>
      </DNABox>
    );
  }

  function onBackdropPressHandler() {
    toggleSearchResultComponent(false)
  }

  return (
    <DNABox appearance="row" alignY="center" style={styles.searchBarWrapper}>
      <DNABox fill>
        <OverflowMenu
          anchor={renderSearchInput}
          visible={displaySearchResults && !isPublisherView && !isDebugView && !isAiSearchActive}
          placement="bottom start"
          fullWidth={deviceMode !== 'tablet'}
          scrollEnabled={true}
          onBackdropPress={onBackdropPressHandler}
          style={util.mergeStyles(
            styles.overflowMenu,
            undefined,
            [{ minWidth: INITIAL_MAX_WIDTH }, deviceMode === 'tablet'],
          )}
        >
          <DocumentSearchProxyProvider value={contextValues}>
            <SearchInputResultsDesktop
              searchText={searchText}
              moveToResultsPage={moveToResultsPage}
              onSelectItem={onSelectItemHandler}
            />
          </DocumentSearchProxyProvider>
        </OverflowMenu>
      </DNABox>
      <AISearchFlowSelector />
      <AISearchHistoryControls />
    </DNABox>
  );
};

const SearchTextContext = createContext<{
  currentSearchString: string;
  setCurrentSearchString:(text: string) => void;}>(null!)

/**
 * Hook to extract the initial search string from the URL path.
 *
 * It determines if the current path is a publisher search results page or a
 * regular search page, then extracts the search query from the URL path or query parameters.
 *
 * @returns {string} The search string found in the URL, or an empty string if no search query is present.
 */
const getInitialSearchFromUrl = (pathname: string, search: string): string => {
  const isMSLSearch = pathname.toLowerCase() === ROUTES.SEARCH_RESULTS_PUBLISHER.SEARCH;
  const isRegularSearch = Boolean(matchPath(pathname, { path: ROUTES.SEARCH_RESULTS.PATH }));

  if (isMSLSearch) {
    return pathname.split('/')[2] || '';
  }

  const params = qs.parse(search, { ignoreQueryPrefix: true });
  if (typeof params.draftSearchText === 'string') return params.draftSearchText;

  if (isRegularSearch) {
    return getSearchRouteText(pathname.split('/')[2], search);
  }

  return '';
};

const useInitialSearchFromUrl = (): string => {
  const { pathname, search } = useLocation();
  return useMemo(() => getInitialSearchFromUrl(pathname, search), [pathname, search]);
};

export const SearchTextContextProvider: React.FC<PropsWithChildren> = (props) => {
  const initialSearchString = useInitialSearchFromUrl();
  const [currentSearchString, setCurrentSearch] = useState(initialSearchString);
  const history = useHistory();

  useEffect(() => history.listen((location, action) => {
    if (action === 'POP') {
      setCurrentSearch(getInitialSearchFromUrl(location.pathname, location.search));
    }
  }), [history]);

  const setCurrentSearchString = (text: string) => setCurrentSearch(text)

  const context = {
    currentSearchString,
    setCurrentSearchString,
  }

  return (
    <SearchTextContext.Provider value={context}>
      {props.children}
    </SearchTextContext.Provider>
  )
}

export const useSearchTextContextProvider = () => useContext(SearchTextContext)

SearchInput.displayName = 'SearchInput';

export default SearchInput;
